import React, { useMemo, useRef, useState } from 'react'
import { useViewStore } from '../Contexts/ViewContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useDirectlineStore } from '../Contexts/DirectlineContext'
import useRefDimensions from '../../hooks/useRefDimensions'
import MicrophoneLoader from '../Loader/MicrophoneLoader'

interface IFrameProps {
  uri: string
  height?: number | string
  width?: number | string
  title?: string
  actions?: Array<{
    type: string
    value: string
    title: string
  }>
}

const IFrame = ({ uri, height, width, title, actions }: IFrameProps): JSX.Element => {
  const isMobile = useViewStore((state) => state.isMobile)
  const currentHeight = useViewStore((state) => state.currentHeight)
  const currentWidth = useViewStore((state) => state.currentWidth)
  const setModalFullscreenContent = useUtilsStore((state) => state.actions.setModalFullscreenContent)
  const sendMessage = useDirectlineStore((state) => state.actions.sendMessage)

  const [documentLoaded, setDocumentLoaded] = useState<boolean>(false)
  const divRef = useRef<HTMLDivElement>(null)
  const dimensions = useRefDimensions(divRef)

  const processedHeight = useMemo<number | string>(() => {
    if (currentHeight > currentWidth) {
      return currentWidth > 960 ? '50%' : '100%'
    }

    return '100%'
  }, [currentHeight, currentWidth])

  const processedWidth = useMemo<number | string>(() => {
    return currentWidth > 960 ? 960 : '100%'
  }, [currentWidth])

  const handleClick = (value: string): void => {
    if (typeof value === 'string') {
      sendMessage(value)
    } else if (typeof value === 'object') {
      sendMessage(undefined, value)
    }
    setModalFullscreenContent(null)
  }

  return (
    <div
      ref={divRef}
      className={`${isMobile ? '' : 'rf-py-8'} rf-flex rf-flex-col rf-justify-center`}
      style={{
        height: height || processedHeight,
        width: width || processedWidth
      }}
    >
      {/* Title */}
      {title && <div className='rf-w-full rf-py-4 rf-px-4 rf-flex rf-flex-row rf-justify-start rf-items-center rf-gap-4 rf-bg-truewhite rf-rounded-t-lg'>{title}</div>}

      {/* Loader */}
      {!documentLoaded && (
        <div className='rf-absolute rf-h-24 rf-w-24 rf-top-[calc(50%-3rem)] rf-left-[calc(50%-3rem)]'>
          <MicrophoneLoader color='secondary' />
        </div>
      )}

      <iframe
        src={uri}
        height={dimensions.height}
        width={dimensions.width}
        style={{
          backgroundColor: '#FFF'
        }}
        onLoad={() => setDocumentLoaded(true)}
      />

      {/* Validation button */}
      {actions?.length && (
        <div className='rf-w-full rf-py-4 rf-flex rf-flex-row rf-justify-center rf-items-center rf-gap-4 rf-bg-truewhite rf-rounded-b-lg'>
          {actions.map((action, key) => {
            return key === 0 ? (
              <button
                key={key}
                className='rf-text-truewhite rf-border rf-border-[#1999B1] rf-bg-[#1999B1] hover:rf-text-[#1999B1] hover:rf-bg-transparent rf-rounded rf-py-1 large:rf-py-2 rf-px-2 large:rf-px-4 rf-text-size-auto'
                onClick={() => handleClick(action.value)}
              >
                {action.title}
              </button>
            ) : (
              <button
                key={key}
                className='rf-text-[#1999B1] rf-border rf-border-[#1999B1] rf-bg-transparent hover:rf-text-truewhite hover:rf-bg-[#1999B1] rf-rounded rf-py-1 large:rf-py-2 rf-px-2 large:rf-px-4 rf-text-size-auto'
                onClick={() => handleClick(action.value)}
              >
                {action.title}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default IFrame
